// ke-story-slider — medicube-style fullscreen story player: mini-PDP panel, side previews,
// swipe navigation + quick-add takeover on mobile, mute toggle, directional slide, progress bar
class KeStorySlider extends HTMLElement {
  connectedCallback() {
    const dataEl = this.querySelector('[data-ke-stories]');
    try { this.stories = JSON.parse(dataEl ? dataEl.textContent : '[]'); } catch (e) { this.stories = []; }

    this.player = this.querySelector('.ke-story-slider__player');
    if (!this.player) return;
    const q = (sel) => this.player.querySelector(sel);

    this.video = q('.ke-story-slider__video');
    this.videoPane = q('.ke-story-slider__video-pane');
    this.badge = q('.ke-story-slider__badge');
    this.pdpEl = q('[data-ke-story-pdp]');
    this.progressFill = q('.ke-story-slider__progress-fill');
    this.muteBtn = q('[data-ke-mute]');
    this.quickaddEl = q('[data-ke-quickadd]');
    this.hintEl = q('.ke-story-slider__hint');
    this.sheet = q('.ke-story-slider__sheet');
    this.sheetContent = q('[data-ke-sheet-content]');
    this.sides = { '-1': q('.ke-story-slider__side--prev'), '1': q('.ke-story-slider__side--next') };
    this.index = 0;
    this.pdpIndex = 0;
    this.qty = 1;
    this.userUnmuted = false;

    this.querySelectorAll('.ke-story-slider__thumb').forEach((t) => {
      t.addEventListener('click', () => this.openPlayer(parseInt(t.dataset.storyIndex, 10) || 0));
    });
    this.player.querySelectorAll('[data-ke-story-close]').forEach((el) => el.addEventListener('click', () => this.closePlayer()));
    const sheetClose = q('[data-ke-sheet-close]');
    if (sheetClose) sheetClose.addEventListener('click', () => this.closeSheet());
    if (this.sheet) this.sheet.addEventListener('click', (e) => { if (e.target === this.sheet) this.closeSheet(); });

    const prev = q('.ke-story-slider__nav--prev');
    const next = q('.ke-story-slider__nav--next');
    if (prev) prev.addEventListener('click', () => this.go(-1));
    if (next) next.addEventListener('click', () => this.go(1));
    Object.keys(this.sides).forEach((dir) => {
      const side = this.sides[dir];
      if (side) side.addEventListener('click', () => this.go(parseInt(dir, 10)));
    });

    if (this.muteBtn) this.muteBtn.addEventListener('click', () => this.toggleMute());

    this.onKeydown = (e) => {
      if (this.player.hidden) return;
      if (e.key === 'Escape') { this.sheet && !this.sheet.hidden ? this.closeSheet() : this.closePlayer(); }
      if (e.key === 'ArrowLeft') this.go(-1);
      if (e.key === 'ArrowRight') this.go(1);
    };
    document.addEventListener('keydown', this.onKeydown);

    // Swipe: horizontal = prev/next story, downward = close (mobile)
    this.touchStart = null;
    this.player.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      this.touchStart = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }, { passive: true });
    this.player.addEventListener('touchend', (e) => {
      if (!this.touchStart || (this.sheet && !this.sheet.hidden)) { this.touchStart = null; return; }
      const dx = e.changedTouches[0].clientX - this.touchStart.x;
      const dy = e.changedTouches[0].clientY - this.touchStart.y;
      this.touchStart = null;
      const vertNav = this.hasAttribute('data-ke-vertical');
      if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy)) {
        this.go(dx < 0 ? 1 : -1);
      } else if (Math.abs(dy) > 60 && Math.abs(dy) > Math.abs(dx)) {
        // With data-ke-vertical the up/down axis navigates like a reels feed: up is the
        // next clip, down the previous. Without it the original behaviour stands, where a
        // downward swipe closes the player -- the homepage story slider still relies on
        // that, so this stays opt-in rather than changing every instance.
        if (vertNav) this.go(dy < 0 ? 1 : -1);
        else if (dy > 90) this.closePlayer();
      }
    }, { passive: true });

    if (this.video) {
      this.video.addEventListener('timeupdate', () => {
        if (!this.video.duration) return;
        this.progressFill.style.width = (this.video.currentTime / this.video.duration) * 100 + '%';
      });
      this.video.addEventListener('ended', () => this.go(1));
    }
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.onKeydown);
    if (this.player && this.player.parentElement === document.body) this.player.remove();
  }

  openPlayer(index) {
    if (!this.stories.length) return;
    // Re-parent to <body> at open time: a transformed/contained ancestor traps
    // position:fixed and collapses the backdrop (page shows through behind the player).
    if (this.player.parentElement !== document.body) document.body.appendChild(this.player);
    this.player.hidden = false;
    document.body.style.overflow = 'hidden';
    this.load(index, 0);
    this.showHintOnce();
  }

  closePlayer() {
    this.player.hidden = true;
    document.body.style.overflow = '';
    if (this.video) { this.video.pause(); this.video.removeAttribute('src'); this.video.load(); }
    this.closeSheet();
  }

  go(dir) {
    if (!this.stories.length) return;
    this.closeSheet();
    const nextIndex = (this.index + dir + this.stories.length) % this.stories.length;
    this.load(nextIndex, dir);
  }

  load(index, dir) {
    this.index = index;
    this.pdpIndex = 0;
    this.qty = 1;
    const story = this.stories[index];
    if (!story) return;

    this.videoPane.classList.remove('anim-up', 'anim-down');
    this.pdpEl.classList.remove('anim-left', 'anim-right');
    if (dir !== 0) {
      void this.videoPane.offsetWidth;
      this.videoPane.classList.add(dir > 0 ? 'anim-up' : 'anim-down');
      this.pdpEl.classList.add(dir > 0 ? 'anim-right' : 'anim-left');
    }

    if (this.video) {
      if (story.video) {
        this.video.src = story.video;
        if (story.poster) this.video.poster = story.poster;
        this.video.muted = !this.userUnmuted;
        this.video.play().catch(() => {});
      } else {
        this.video.removeAttribute('src');
        this.video.load();
        if (story.poster) this.video.poster = story.poster;
      }
    }
    this.updateMuteBtn();
    this.progressFill.style.width = '0%';

    const products = story.products || [];
    if (this.badge) this.badge.hidden = !(products[0] && products[0].compare);
    this.updateSides();
    this.renderPdp();
    this.renderQuickadd();
  }

  updateSides() {
    const n = this.stories.length;
    [-1, 1].forEach((dir) => {
      const side = this.sides[String(dir)];
      if (!side) return;
      if (n < 2) { side.hidden = true; return; }
      const neighbor = this.stories[(this.index + dir + n) % n];
      const img = side.querySelector('img');
      if (neighbor && neighbor.poster && img) { img.src = neighbor.poster; side.hidden = false; }
      else side.hidden = !neighbor;
    });
  }

  toggleMute() {
    if (!this.video) return;
    this.userUnmuted = this.video.muted;
    this.video.muted = !this.video.muted;
    this.updateMuteBtn();
  }

  updateMuteBtn() {
    if (!this.muteBtn || !this.video) return;
    const on = !this.video.muted;
    this.muteBtn.classList.toggle('is-on', on);
    this.muteBtn.setAttribute('aria-pressed', String(on));
  }

  showHintOnce() {
    if (!this.hintEl || window.matchMedia('(min-width: 750px)').matches) return;
    let seen = false;
    try { seen = sessionStorage.getItem('keStoryHint') === '1'; } catch (e) { /* private mode */ }
    if (seen) return;
    try { sessionStorage.setItem('keStoryHint', '1'); } catch (e) { /* private mode */ }
    this.hintEl.hidden = false;
    this.hintEl.classList.remove('is-fading');
    setTimeout(() => this.hintEl.classList.add('is-fading'), 2200);
    setTimeout(() => { this.hintEl.hidden = true; }, 2900);
  }

  el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  /* ---------- Desktop mini-PDP panel ---------- */
  renderPdp() {
    const wrap = this.pdpEl;
    if (!wrap) return;
    wrap.textContent = '';
    const products = (this.stories[this.index] || {}).products || [];
    if (!products.length) return;
    const p = products[this.pdpIndex % products.length];

    const price = this.el('p', 'ke-story-slider__pdp-price');
    const strong = this.el('strong', null, p.price);
    price.appendChild(strong);
    if (p.compare) price.appendChild(this.el('s', null, p.compare));
    wrap.appendChild(price);

    wrap.appendChild(this.el('h3', 'ke-story-slider__pdp-title', p.title));

    const media = this.el('div', 'ke-story-slider__pdp-media');
    const img = this.el('img');
    if (p.image) img.src = p.image;
    img.alt = p.title;
    media.appendChild(img);
    if (products.length > 1) {
      const mPrev = this.el('button', 'ke-story-slider__pdp-arrow ke-story-slider__pdp-arrow--prev', '‹');
      mPrev.type = 'button';
      mPrev.setAttribute('aria-label', 'Previous product');
      mPrev.addEventListener('click', () => this.pdpGo(-1));
      const mNext = this.el('button', 'ke-story-slider__pdp-arrow ke-story-slider__pdp-arrow--next', '›');
      mNext.type = 'button';
      mNext.setAttribute('aria-label', 'Next product');
      mNext.addEventListener('click', () => this.pdpGo(1));
      media.appendChild(mPrev);
      media.appendChild(mNext);
    }
    wrap.appendChild(media);

    const dots = this.el('div', 'ke-story-slider__pdp-dots');
    products.forEach((_, i) => {
      const dot = this.el('button', 'ke-story-slider__pdp-dot' + (i === this.pdpIndex ? ' is-active' : ''));
      dot.type = 'button';
      dot.setAttribute('aria-label', 'Product ' + (i + 1));
      dot.addEventListener('click', () => { this.pdpIndex = i; this.qty = 1; this.renderPdp(); });
      dots.appendChild(dot);
    });
    wrap.appendChild(dots);

    wrap.appendChild(this.el('p', 'ke-story-slider__pdp-label', 'Quantity'));
    const qtyBox = this.el('div', 'ke-story-slider__qty');
    const minus = this.el('button', null, '–');
    minus.type = 'button';
    minus.setAttribute('aria-label', 'Decrease quantity');
    const value = this.el('span', 'ke-story-slider__qty-value', String(this.qty));
    const plus = this.el('button', null, '+');
    plus.type = 'button';
    plus.setAttribute('aria-label', 'Increase quantity');
    minus.addEventListener('click', () => { this.qty = Math.max(1, this.qty - 1); value.textContent = String(this.qty); });
    plus.addEventListener('click', () => { this.qty += 1; value.textContent = String(this.qty); });
    qtyBox.appendChild(minus);
    qtyBox.appendChild(value);
    qtyBox.appendChild(plus);
    wrap.appendChild(qtyBox);

    if (p.description) {
      wrap.appendChild(this.el('p', 'ke-story-slider__pdp-label', 'Description'));
      wrap.appendChild(this.el('div', 'ke-story-slider__pdp-desc', p.description));
    }

    const footer = this.el('div', 'ke-story-slider__pdp-footer');
    const info = this.el('a', 'ke-story-slider__pdp-info', 'More info');
    info.href = p.url;
    const add = this.el('button', 'ke-story-slider__add', p.available ? 'Add to Cart' : 'Sold out');
    add.type = 'button';
    if (!p.available) add.disabled = true;
    add.addEventListener('click', () => this.addToCart(p, add, this.qty));
    footer.appendChild(info);
    footer.appendChild(add);
    wrap.appendChild(footer);
  }

  pdpGo(dir) {
    const products = (this.stories[this.index] || {}).products || [];
    if (!products.length) return;
    this.pdpIndex = (this.pdpIndex + dir + products.length) % products.length;
    this.qty = 1;
    this.renderPdp();
  }

  /* ---------- Mobile quick-add card ---------- */
  renderQuickadd() {
    const wrap = this.quickaddEl;
    if (!wrap) return;
    wrap.textContent = '';
    const products = (this.stories[this.index] || {}).products || [];
    if (!products.length) { wrap.hidden = true; return; }
    const p0 = products[0];

    if (p0.compare) wrap.appendChild(this.el('span', 'ke-story-slider__quickadd-badge', 'Sale'));

    const row = this.el('div', 'ke-story-slider__quickadd-row');
    const thumb = this.el('img', 'ke-story-slider__quickadd-thumb');
    if (p0.image) thumb.src = p0.image;
    thumb.alt = p0.title;
    const meta = this.el('div', 'ke-story-slider__quickadd-meta');
    meta.appendChild(this.el('span', 'ke-story-slider__quickadd-count', products.length > 1 ? products.length + ' items' : p0.price));
    meta.appendChild(this.el('span', 'ke-story-slider__quickadd-title', p0.title));
    row.appendChild(thumb);
    row.appendChild(meta);
    wrap.appendChild(row);

    const btn = this.el('button', 'ke-story-slider__add', p0.available || products.length > 1 ? 'Quick add' : 'Sold out');
    btn.type = 'button';
    if (!p0.available && products.length === 1) btn.disabled = true;
    btn.addEventListener('click', () => {
      if (products.length > 1) this.openSheet(products);
      else this.addToCart(p0, btn, 1);
    });
    wrap.appendChild(btn);
    wrap.hidden = false;
  }

  openSheet(products) {
    if (!this.sheet) return;
    this.sheetContent.textContent = '';
    products.forEach((p) => {
      const item = this.el('div', 'ke-story-slider__sheet-item');
      const img = this.el('img', 'ke-story-slider__sheet-img');
      if (p.image) img.src = p.image;
      img.alt = p.title;
      const info = this.el('div', 'ke-story-slider__sheet-info');
      info.appendChild(this.el('p', 'ke-story-slider__sheet-title', p.title));
      const price = this.el('span', 'ke-story-slider__sheet-price', p.price);
      info.appendChild(price);
      if (p.compare) info.appendChild(this.el('span', 'ke-story-slider__sheet-compare', p.compare));
      const add = this.el('button', 'ke-story-slider__add', p.available ? 'Add' : 'Sold out');
      add.type = 'button';
      if (!p.available) add.disabled = true;
      add.addEventListener('click', () => this.addToCart(p, add, 1));
      item.appendChild(img);
      item.appendChild(info);
      item.appendChild(add);
      this.sheetContent.appendChild(item);
    });
    this.sheet.hidden = false;
  }

  closeSheet() { if (this.sheet) this.sheet.hidden = true; }

  addToCart(p, button, qty) {
    if (!p.id) return;
    const original = button.textContent;
    button.textContent = '…';
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: p.id, quantity: qty || 1 }] })
    })
      .then((r) => {
        button.classList.add('is-added');
        button.textContent = r.ok ? '✓ Added' : 'Error';
        setTimeout(() => { button.classList.remove('is-added'); button.textContent = original; }, 2000);
        if (r.ok) document.dispatchEvent(new CustomEvent('ke:cart:added'));
      })
      .catch(() => { button.textContent = original; });
  }
}
customElements.define('ke-story-slider', KeStorySlider);
